import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faRocket, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useCreateSoundProject } from '../services/soundProjectService';
import styles from './CreateSoundProjectModal.module.css';

interface CreateSoundProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (projectId: string) => void;
}

const CreateSoundProjectModal: React.FC<CreateSoundProjectModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [projectData, setProjectData] = useState({
    title: '',
    description: ''
  });

  const { createProject, isLoading, error } = useCreateSoundProject();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!projectData.title.trim()) return;

    try {
      const result = await createProject({
        title: projectData.title.trim(),
        description: projectData.description.trim() || undefined,
        trackType: 'sound'
      });

      if (result.success) {
        // Reset form
        setProjectData({ title: '', description: '' });
        onClose();
        
        // Call success callback if provided
        if (onSuccess) {
          onSuccess(result.data.projectId);
        }
      }
    } catch (err) {
      // Error is handled by the hook
      console.error('Create project failed:', err);
    }
  };

  const handleClose = () => {
    setProjectData({ title: '', description: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FontAwesomeIcon icon={faRocket} className={styles.modalTitleIcon} />
            Create New Sound Project
          </h2>
          <button
            className={styles.modalCloseButton}
            onClick={handleClose}
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Content */}
          <div className={styles.modalContent}>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>
                Project Title *
              </label>
              <input
                type="text"
                placeholder="Enter your sound project title..."
                value={projectData.title}
                onChange={(e) => setProjectData({ ...projectData, title: e.target.value })}
                className={styles.inputField}
                autoFocus
                required
              />
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>
                Description (Optional)
              </label>
              <textarea
                placeholder="Describe what this sound project is about..."
                value={projectData.description}
                onChange={(e) => setProjectData({ ...projectData, description: e.target.value })}
                rows={3}
                className={styles.textareaField}
              />
            </div>

            {error && (
              <div className={styles.errorMessage}>
                {error}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.modalCancelButton}
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.modalCreateButton}
              disabled={isLoading || !projectData.title.trim()}
            >
              <FontAwesomeIcon icon={faPlus} />
              {isLoading ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSoundProjectModal;
